import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import s from '../DietPlanBuilder.module.css';
import { Icon } from '../ui';
import { copy } from '../../copy';
import { fmtG, fmtInt } from '../../lib/format';
import { useFoodSearch } from '../../hooks/useFoodSearch';
import type { Builder } from '../../hooks/useDietPlanBuilder';
import type { Food, SlotKey } from '../../types';

type Pos = { left: number; width: number; maxHeight: number; top?: number; bottom?: number };

/**
 * Draft food row: same columns as existing foods, with a search field and floating results.
 */
export function InlineFoodSearch({
  api,
  slot,
  onPick,
  onClose,
}: {
  api: Builder['api'];
  slot: SlotKey;
  onPick: (food: Food) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [draftQty, setDraftQty] = useState(1);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<Pos | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { list, loading, error } = useFoodSearch(api, q, slot, true);
  const preview = list[active];

  const place = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const r = input.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 16;
    const above = r.top - 16;
    const up = below < 220 && above > below;
    setPos({
      left: r.left,
      width: r.width,
      maxHeight: Math.max(140, Math.min(360, up ? above : below)),
      ...(up ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
    });
  }, []);

  useLayoutEffect(() => {
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [place]);

  useEffect(() => { setActive(0); }, [q, list]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rowRef.current?.contains(t) || listRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const pick = (food: Food) => onPick({ ...food, qty: draftQty });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(list.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const food = list[active];
      if (food) pick(food);
    }
  };

  const bumpQty = (delta: number) => {
    setDraftQty(q => Math.max(0.25, Math.round((q + delta) * 100) / 100));
  };

  const showPreview = preview && draftQty > 0;
  const kcal = showPreview ? fmtInt(preview.per[0] * draftQty) : '0';
  const pg = showPreview ? fmtG(preview.per[1] * draftQty) : '0';
  const cg = showPreview ? fmtG(preview.per[2] * draftQty) : '0';
  const fg = showPreview ? fmtG(preview.per[3] * draftQty) : '0';
  const unit = showPreview ? preview.unit : '—';

  return (
    <div ref={rowRef} className={`${s.foodsTableRow} ${s.foodsSearchRow}`}>
      <div className={s.foodsSearchCell}>
        <input
          ref={inputRef}
          autoFocus
          className={`${s.foodsNameInput} ${s.foodsSearchInput}`}
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={copy.search.placeholder}
          aria-label={copy.search.placeholder}
          role="combobox"
          aria-expanded
          aria-autocomplete="list"
        />
      </div>
      <div className={s.foodsQty}>
        <button type="button" className={s.stepBtnXs} aria-label="Less" onClick={() => bumpQty(-(draftQty > 1 ? 0.5 : 0.25))}>
          <Icon.Minus size={14} />
        </button>
        <input
          className={s.foodsQtyInput}
          type="number"
          step={0.5}
          min={0.25}
          value={draftQty}
          onChange={e => setDraftQty(Math.max(0.25, parseFloat(e.target.value) || 0.25))}
          aria-label="Quantity"
        />
        <button type="button" className={s.stepBtnXs} aria-label="More" onClick={() => bumpQty(draftQty >= 1 ? 0.5 : 0.25)}>
          <Icon.Plus size={14} />
        </button>
      </div>
      <span className={s.foodsUnit}>{unit}</span>
      <span className={s.foodsNumStrong}>{kcal}</span>
      <span className={s.foodsNum}>{pg}</span>
      <span className={s.foodsNum}>{cg}</span>
      <span className={s.foodsNum}>{fg}</span>
      <button type="button" className={s.mealsTrashBtn} onClick={onClose} aria-label="Cancel">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
        </svg>
      </button>

      {pos && (
        <div
          ref={listRef}
          className={s.foodSearchPop}
          role="listbox"
          style={{ left: pos.left, width: pos.width, maxHeight: pos.maxHeight, top: pos.top, bottom: pos.bottom }}
        >
          <div className={s.foodSearchHint}>{q.trim() ? copy.search.db : copy.search.often}</div>
          {loading && !list.length && <div className={s.foodSearchMsg}>…</div>}
          {error && <div className={s.foodSearchMsg} data-error>{error.message}</div>}
          {!loading && !error && !list.length && <div className={s.foodSearchMsg}>{copy.search.empty(q)}</div>}
          {list.map((r, i) => (
            <button
              type="button"
              key={`${r.id ?? r.name}-${i}`}
              role="option"
              aria-selected={i === active}
              data-idx={i}
              data-active={i === active}
              className={s.foodSearchItem}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(r)}
            >
              <span className={s.foodSearchName}>
                <b>{r.name}</b>
                <small>per {fmtG(r.qty)} {r.unit}</small>
              </span>
              <span className={s.foodSearchKcal}>{fmtInt(r.per[0] * r.qty)} kcal</span>
              <span className={s.foodSearchMacros}>
                P {fmtG(r.per[1] * r.qty)}&nbsp;&nbsp;C {fmtG(r.per[2] * r.qty)}&nbsp;&nbsp;F {fmtG(r.per[3] * r.qty)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
